import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  User, Mail, Phone, Lock, Key, LogOut, Loader2, AlertCircle, 
  CheckCircle, Shield, Coffee, Edit2, Save, X, Eye, EyeOff,
  Clock, Calendar, CalendarOff, Smartphone, Wallet, Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

/**
 * Staff Profile Page
 * Allows staff to view and update their account settings
 */
export default function StaffProfilePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null); // 'email', 'phone', 'password', 'pin'
  
  // Form states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('sms');
  
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('staff_token');

  useEffect(() => {
    if (!token) {
      navigate('/staff/login');
      return;
    }
    fetchProfile();
  }, [token, navigate]);

  const fetchProfile = async () => {
    try {
      const res = await api.get(`/staff-portal/profile?token=${token}`);
      setProfile(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('staff_token');
        localStorage.removeItem('staff_info');
        navigate('/staff/login');
        return;
      }
      toast.error(err.response?.data?.detail || err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post(`/staff-portal/logout?token=${token}`);
    } catch (e) {
      // Ignore logout errors
    }
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_info');
    toast.success('Logged out successfully');
    navigate('/staff/login');
  };

  const resetForms = () => {
    setActiveSection(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setNewEmail('');
    setNewPhone('');
    setVerificationCode('');
    setVerificationSent(false);
    setCurrentPin('');
    setNewPin('');
    setConfirmPin('');
    setError('');
  };

  // Password Update
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setActionLoading(true);
    try {
      await api.post(`/staff-portal/update-password?token=${token}&current_password=${encodeURIComponent(currentPassword)}&new_password=${encodeURIComponent(newPassword)}&confirm_password=${encodeURIComponent(confirmPassword)}`);
      
      toast.success('Password updated successfully!');
      resetForms();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to update password');
    } finally {
      setActionLoading(false);
    }
  };

  // Request Verification Code
  const requestVerification = async (action) => {
    setActionLoading(true);
    setError('');
    try {
      const res = await api.post(`/staff-portal/request-verification?token=${token}&action=${action}&method=${verificationMethod}`);
      
      setVerificationSent(true);
      toast.success(`Verification code sent via ${res.data.method}`);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to send verification');
    } finally {
      setActionLoading(false);
    }
  };

  // Email Update
  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!newEmail || !verificationCode || !currentPassword) {
      setError('Please fill in all fields');
      return;
    }

    setActionLoading(true);
    try {
      await api.post(`/staff-portal/update-email?token=${token}&new_email=${encodeURIComponent(newEmail)}&verification_code=${verificationCode}&current_password=${encodeURIComponent(currentPassword)}`);
      
      toast.success('Email updated successfully!');
      resetForms();
      fetchProfile();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to update email');
    } finally {
      setActionLoading(false);
    }
  };

  // Phone Update
  const handleUpdatePhone = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!newPhone || !verificationCode || !currentPassword) {
      setError('Please fill in all fields');
      return;
    }

    setActionLoading(true);
    try {
      await api.post(`/staff-portal/update-phone?token=${token}&new_phone=${encodeURIComponent(newPhone)}&verification_code=${verificationCode}&current_password=${encodeURIComponent(currentPassword)}`);
      
      toast.success('Phone number updated successfully!');
      resetForms();
      fetchProfile();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to update phone');
    } finally {
      setActionLoading(false);
    }
  };

  // PIN Update
  const handleUpdatePin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPin !== confirmPin) {
      setError('PINs do not match');
      return;
    }
    if (newPin.length !== 6 || !/^\d+$/.test(newPin)) {
      setError('PIN must be exactly 6 digits');
      return;
    }

    setActionLoading(true);
    try {
      await api.post(`/staff-portal/update-pin?token=${token}`, {
        current_pin: currentPin,
        new_pin: newPin,
        confirm_pin: confirmPin
      });
      
      toast.success('PIN updated successfully!');
      resetForms();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to update PIN');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center">
              <Coffee className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900 dark:text-white">Staff Portal</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Manage your account</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Profile Header Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{profile?.name}</h2>
              <p className="text-gray-500 dark:text-gray-400 capitalize">{profile?.role}</p>
            </div>
          </div>
        </div>

        {/* Staff Tab Balance */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Staff Meal Tab</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">50% discount meals deducted from pay</p>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-2xl font-bold ${(profile?.tab_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                ${(profile?.tab_balance || 0).toFixed(2)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Current Balance</div>
            </div>
          </div>
          
          {/* Recent Tab Entries */}
          {profile?.recent_tabs && profile.recent_tabs.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Recent Activity
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {profile.recent_tabs.map((entry, idx) => (
                  <div key={entry.id || idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-sm">
                    <div>
                      <span className="text-gray-700 dark:text-gray-300">{entry.description || 'Staff meal'}</span>
                      <span className="text-gray-400 ml-2 text-xs">
                        {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <span className={entry.type === 'charge' ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                      {entry.type === 'charge' ? '+' : '-'}${Math.abs(entry.amount).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* No activity message */}
          {(!profile?.recent_tabs || profile.recent_tabs.length === 0) && (profile?.tab_balance || 0) === 0 && (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
              No staff meal charges this pay period
            </div>
          )}
          
          {/* Last cleared info */}
          {profile?.last_tab_cleared_at && (
            <div className="text-xs text-gray-400 mt-3 text-center">
              Last cleared: {new Date(profile.last_tab_cleared_at).toLocaleDateString()}
            </div>
          )}
        </div>

        {/* Quick Actions - Workforce Features */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Time Clock */}
            <Link 
              to="/staff/clock" 
              className="flex flex-col items-center p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-colors border border-teal-200 dark:border-teal-800"
            >
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mb-2">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-teal-700 dark:text-teal-300">Time Clock</span>
              <span className="text-xs text-teal-600 dark:text-teal-400">Clock In/Out</span>
            </Link>

            {/* Schedule */}
            <Link 
              to="/staff/schedule" 
              className="flex flex-col items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border border-blue-200 dark:border-blue-800"
            >
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-2">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Schedule</span>
              <span className="text-xs text-blue-600 dark:text-blue-400">View Shifts</span>
            </Link>

            {/* Manage Schedule - Only for managers */}
            {['owner', 'mgr-sup', 'manager', 'admin'].includes(profile?.role?.toLowerCase()) && (
              <Link 
                to="/staff/manage-schedule" 
                className="flex flex-col items-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors border border-amber-200 dark:border-amber-800"
              >
                <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mb-2">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Manage</span>
                <span className="text-xs text-amber-600 dark:text-amber-400">Create Schedules</span>
              </Link>
            )}

            {/* Weekly Roster - Only for managers */}
            {['owner', 'mgr-sup', 'manager', 'admin'].includes(profile?.role?.toLowerCase()) && (
              <Link 
                to="/staff/roster" 
                className="flex flex-col items-center p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors border border-indigo-200 dark:border-indigo-800"
              >
                <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mb-2">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Roster</span>
                <span className="text-xs text-indigo-600 dark:text-indigo-400">Weekly View</span>
              </Link>
            )}

            {/* Time Off */}
            <Link 
              to="/staff/time-off" 
              className="flex flex-col items-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors border border-purple-200 dark:border-purple-800"
            >
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mb-2">
                <CalendarOff className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Time Off</span>
              <span className="text-xs text-purple-600 dark:text-purple-400">Request PTO</span>
            </Link>

            {/* Device Registration */}
            <Link 
              to="/staff/device" 
              className="flex flex-col items-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors border border-amber-200 dark:border-amber-800"
            >
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mb-2">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-amber-700 dark:text-amber-300">My Device</span>
              <span className="text-xs text-amber-600 dark:text-amber-400">Register</span>
            </Link>
          </div>
        </div>

        {/* Account Settings Header */}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Settings</h3>
        <div className="space-y-4">
          {/* Email Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
              onClick={() => activeSection === 'email' ? resetForms() : setActiveSection('email')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Email Address</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{profile?.email || 'Not set'}</p>
                </div>
              </div>
              <Edit2 className="w-5 h-5 text-gray-400" />
            </div>
            
            {activeSection === 'email' && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
                <form onSubmit={handleUpdateEmail} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 rounded-lg text-red-700 dark:text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}
                  
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="New email address"
                    className="bg-white dark:bg-gray-800"
                  />
                  
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password (for verification)"
                    className="bg-white dark:bg-gray-800"
                  />
                  
                  {!verificationSent ? (
                    <div className="flex gap-2">
                      <select 
                        value={verificationMethod}
                        onChange={(e) => setVerificationMethod(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="sms">SMS</option>
                        <option value="email">Email</option>
                      </select>
                      <Button
                        type="button"
                        onClick={() => requestVerification('email_change')}
                        disabled={actionLoading || !newEmail}
                        className="flex-1"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Verification Code'}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        className="bg-white dark:bg-gray-800"
                      />
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={resetForms}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={actionLoading} className="flex-1 bg-teal-600 hover:bg-teal-700">
                          {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Email'}
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              </div>
            )}
          </div>

          {/* Phone Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
              onClick={() => activeSection === 'phone' ? resetForms() : setActiveSection('phone')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Phone Number</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{profile?.phone || 'Not set'}</p>
                </div>
              </div>
              <Edit2 className="w-5 h-5 text-gray-400" />
            </div>
            
            {activeSection === 'phone' && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
                <form onSubmit={handleUpdatePhone} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 rounded-lg text-red-700 dark:text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300">
                      +679
                    </span>
                    <Input
                      type="tel"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 7))}
                      placeholder="7-digit number"
                      maxLength={7}
                      className="flex-1 bg-white dark:bg-gray-800"
                    />
                  </div>
                  
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password (for verification)"
                    className="bg-white dark:bg-gray-800"
                  />
                  
                  {!verificationSent ? (
                    <div className="flex gap-2">
                      <select 
                        value={verificationMethod}
                        onChange={(e) => setVerificationMethod(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="sms">SMS</option>
                        <option value="email">Email</option>
                      </select>
                      <Button
                        type="button"
                        onClick={() => requestVerification('phone_change')}
                        disabled={actionLoading || !newPhone}
                        className="flex-1"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Verification Code'}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        className="bg-white dark:bg-gray-800"
                      />
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={resetForms}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={actionLoading} className="flex-1 bg-teal-600 hover:bg-teal-700">
                          {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Phone'}
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              </div>
            )}
          </div>

          {/* Password Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
              onClick={() => activeSection === 'password' ? resetForms() : setActiveSection('password')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Password</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Change your login password</p>
                </div>
              </div>
              <Edit2 className="w-5 h-5 text-gray-400" />
            </div>
            
            {activeSection === 'password' && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
                <form onSubmit={handleUpdatePassword} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 rounded-lg text-red-700 dark:text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}
                  
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Current password"
                      className="pr-10 bg-white dark:bg-gray-800"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 8 characters)"
                    className="bg-white dark:bg-gray-800"
                  />
                  
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="bg-white dark:bg-gray-800"
                  />
                  
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-sm text-red-600 dark:text-red-400">Passwords do not match</p>
                  )}
                  
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={resetForms}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={actionLoading || newPassword !== confirmPassword || newPassword.length < 8}
                      className="flex-1 bg-teal-600 hover:bg-teal-700"
                    >
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Password'}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* PIN Section - All staff have PINs for device tracking */}
          {profile?.has_pin && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                onClick={() => activeSection === 'pin' ? resetForms() : setActiveSection('pin')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                    <Key className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Device Login PIN</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      For all stations and displays
                    </p>
                  </div>
                </div>
                <Edit2 className="w-5 h-5 text-gray-400" />
              </div>
              
              {activeSection === 'pin' && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
                  <form onSubmit={handleUpdatePin} className="space-y-4">
                    {error && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 rounded-lg text-red-700 dark:text-red-400 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                      </div>
                    )}
                    
                    <Input
                      type="password"
                      value={currentPin}
                      onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Current PIN"
                      maxLength={6}
                      className="bg-white dark:bg-gray-800"
                    />
                    
                    <Input
                      type="password"
                      value={newPin}
                      onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="New 6-digit PIN"
                      maxLength={6}
                      className="bg-white dark:bg-gray-800"
                    />
                    
                    <Input
                      type="password"
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Confirm new PIN"
                      maxLength={6}
                      className="bg-white dark:bg-gray-800"
                    />
                    
                    {newPin && confirmPin && newPin !== confirmPin && (
                      <p className="text-sm text-red-600 dark:text-red-400">PINs do not match</p>
                    )}
                    
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={resetForms}>
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={actionLoading || newPin !== confirmPin || newPin.length !== 6}
                        className="flex-1 bg-teal-600 hover:bg-teal-700"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update PIN'}
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Security Note */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <div className="flex gap-3">
            <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-300">Security Tip</p>
              <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                For your security, sensitive changes require verification via email or SMS. 
                Never share your password or PIN with anyone.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
